// scanner-fp: `useCallback` deps intentionally omit `api` (module singleton), setters (stable), and local vars. See /app/SECURITY_SCANNER_IGNORE.md §4.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Database, Plus, Pencil, Trash2, Search, Download, Upload,
  ToggleLeft, ToggleRight, Folder, X, ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";

/** One admin page that renders every master type using its schema. */
export default function Masters() {
  const [schemas, setSchemas] = useState([]);
  const [activeType, setActiveType] = useState("");
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [formData, setFormData] = useState({});
  const [importOpen, setImportOpen] = useState(false);
  const [csvText, setCsvText] = useState("");
  const [importResult, setImportResult] = useState(null);

  const loadSchemas = useCallback(async () => {
    const r = await api.get("/masters/schemas");
    setSchemas(r.data);
    if (!activeType && r.data.length) setActiveType(r.data[0].entity_type);
  }, [activeType]);

  const loadRows = useCallback(async () => {
    if (!activeType) return;
    const r = await api.get(`/masters/${activeType}`, { params: { q: query } });
    setRows(r.data);
  }, [activeType, query]);

  useEffect(() => { loadSchemas(); }, [loadSchemas]);
  useEffect(() => { loadRows(); }, [loadRows]);

  const grouped = useMemo(() => {
    const g = {};
    schemas.forEach((s) => {
      g[s.group] = g[s.group] || [];
      g[s.group].push(s);
    });
    return g;
  }, [schemas]);

  const currentSchema = useMemo(
    () => schemas.find((s) => s.entity_type === activeType),
    [schemas, activeType],
  );

  const openCreate = () => {
    setEditRow(null);
    setFormData({});
    setDialogOpen(true);
  };
  const openEdit = (row) => {
    setEditRow(row);
    setFormData({ ...(row.data || {}) });
    setDialogOpen(true);
  };
  const handleSave = async () => {
    try {
      if (editRow) {
        await api.put(`/masters/${activeType}/${editRow.id}`, { data: formData, active: editRow.active });
        toast.success("Updated");
      } else {
        await api.post(`/masters/${activeType}`, { data: formData });
        toast.success("Created");
      }
      setDialogOpen(false);
      loadSchemas();
      loadRows();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };
  const handleDelete = async (row) => {
    if (!window.confirm("Delete this row?")) return;
    await api.delete(`/masters/${activeType}/${row.id}`);
    toast.success("Deleted");
    loadSchemas();
    loadRows();
  };
  const handleToggle = async (row) => {
    await api.post(`/masters/${activeType}/${row.id}/toggle`);
    loadRows();
  };
  const handleExport = () => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/masters/export/${activeType}.csv`, "_blank");
  };
  const handleImport = async () => {
    try {
      const r = await api.post(`/masters/import/${activeType}`, { csv: csvText });
      setImportResult(r.data);
      toast.success(`Imported ${r.data.added}, skipped ${r.data.skipped}`);
      loadSchemas();
      loadRows();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="overline text-secondary">Admin · Data</div>
          <h1 className="mt-1 font-display text-4xl font-light tracking-tight flex items-center gap-3">
            <Database className="w-7 h-7 text-primary" /> Master Data
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage all reference data — makes, models, plans, employees, templates and 20+ more.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={handleExport} data-testid="masters-export-btn">
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
          <Button variant="outline" size="sm" onClick={() => setImportOpen(true)} data-testid="masters-import-btn">
            <Upload className="w-4 h-4 mr-2" /> Import CSV
          </Button>
          <Button size="sm" onClick={openCreate} data-testid="masters-new-btn" disabled={!currentSchema}>
            <Plus className="w-4 h-4 mr-2" /> New {currentSchema?.label || ""}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* left rail */}
        <aside className="col-span-12 lg:col-span-3 xl:col-span-3">
          <div className="rounded-xl border border-border bg-card p-3 max-h-[70vh] overflow-y-auto sticky top-4">
            {Object.entries(grouped).map(([grp, list]) => (
              <div key={grp} className="mb-3">
                <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground px-2 py-1">
                  <Folder className="w-3.5 h-3.5" /> {grp}
                </div>
                <div className="space-y-0.5">
                  {list.map((s) => (
                    <button
                      key={s.entity_type}
                      onClick={() => setActiveType(s.entity_type)}
                      data-testid={`master-type-${s.entity_type}`}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center justify-between transition-colors ${
                        activeType === s.entity_type
                          ? "bg-primary text-primary-foreground shadow-teal-glow"
                          : "hover:bg-muted"
                      }`}
                    >
                      <span>{s.label}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                        activeType === s.entity_type
                          ? "bg-primary-foreground/20"
                          : "bg-muted-foreground/10"
                      }`}>{s.count}</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </aside>

        {/* right content */}
        <section className="col-span-12 lg:col-span-9 xl:col-span-9 space-y-4">
          {currentSchema && (
            <>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-xs text-muted-foreground flex items-center gap-1">
                    {currentSchema.group} <ChevronRight className="w-3 h-3" />
                  </div>
                  <h2 className="text-xl font-semibold">{currentSchema.label} ({rows.length})</h2>
                </div>
                <div className="relative w-full sm:w-64">
                  <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-muted-foreground" />
                  <Input
                    placeholder="Search…"
                    className="pl-9"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    data-testid="masters-search-input"
                  />
                </div>
              </div>

              <div className="rounded-xl border border-border bg-card overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {currentSchema.fields.slice(0, 4).map((f) => (
                        <TableHead key={f.key}>{f.label}</TableHead>
                      ))}
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={currentSchema.fields.slice(0, 4).length + 2} className="text-center text-muted-foreground py-8">
                          No rows yet — click “New {currentSchema.label}” to add one.
                        </TableCell>
                      </TableRow>
                    )}
                    {rows.map((row) => (
                      <TableRow key={row.id} data-testid={`master-row-${row.id}`}>
                        {currentSchema.fields.slice(0, 4).map((f) => (
                          <TableCell key={f.key}>
                            <span className="text-sm">
                              {(() => {
                                const v = (row.data || {})[f.key];
                                if (v == null || v === "") return "—";
                                if (typeof v === "string" && v.length > 60) return v.slice(0, 60) + "…";
                                return String(v);
                              })()}
                            </span>
                          </TableCell>
                        ))}
                        <TableCell>
                          <Badge variant={row.active ? "default" : "secondary"}>
                            {row.active ? "Active" : "Inactive"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right space-x-1">
                          <Button size="icon" variant="ghost" onClick={() => handleToggle(row)} title={row.active ? "Deactivate" : "Activate"}>
                            {row.active ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4 text-muted-foreground" />}
                          </Button>
                          <Button size="icon" variant="ghost" onClick={() => openEdit(row)} data-testid={`master-edit-${row.id}`}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button size="icon" variant="ghost" onClick={() => handleDelete(row)} data-testid={`master-delete-${row.id}`}>
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </section>
      </div>

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editRow ? "Edit" : "New"} {currentSchema?.label}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {currentSchema?.fields.map((f) => (
              <div key={f.key} className={f.type === "text" ? "sm:col-span-2" : ""}>
                <Label className="text-xs">
                  {f.label} {f.required && <span className="text-destructive">*</span>}
                </Label>
                {f.type === "text" ? (
                  <Textarea
                    value={formData[f.key] || ""}
                    onChange={(e) => setFormData({ ...formData, [f.key]: e.target.value })}
                    rows={3}
                    data-testid={`master-form-${f.key}`}
                  />
                ) : f.type === "select" ? (
                  <Select
                    value={formData[f.key] || ""}
                    onValueChange={(v) => setFormData({ ...formData, [f.key]: v })}
                  >
                    <SelectTrigger data-testid={`master-form-${f.key}`}>
                      <SelectValue placeholder={`Choose ${f.label}`} />
                    </SelectTrigger>
                    <SelectContent>
                      {(f.choices || []).map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    type={f.type === "number" ? "number" : "text"}
                    value={formData[f.key] ?? ""}
                    onChange={(e) => setFormData({ ...formData, [f.key]: e.target.value })}
                    data-testid={`master-form-${f.key}`}
                  />
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} data-testid="master-form-save">
              {editRow ? "Save changes" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import CSV dialog */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Import CSV → {currentSchema?.label}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Header row required. Columns: {currentSchema?.fields.map(f => f.key).join(", ")}
            </p>
            <Textarea
              rows={10}
              placeholder="Paste CSV content here…"
              value={csvText}
              onChange={(e) => setCsvText(e.target.value)}
              data-testid="master-import-textarea"
            />
            {importResult && (
              <div className="text-xs bg-muted rounded p-3 space-y-1">
                <div>Added: <b>{importResult.added}</b> · Skipped: <b>{importResult.skipped}</b></div>
                {importResult.errors?.length > 0 && (
                  <details><summary className="cursor-pointer">Errors ({importResult.errors.length})</summary>
                    <ul className="pl-4 list-disc">{importResult.errors.map((e) => <li key={e}>{e}</li>)}</ul>
                  </details>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setImportOpen(false); setImportResult(null); setCsvText(""); }}>Close</Button>
            <Button onClick={handleImport} disabled={!csvText.trim()} data-testid="master-import-run">
              <Upload className="w-4 h-4 mr-2" /> Import
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
